/* eslint-disable eqeqeq */
/**
 * Phase 6 Vendor-Port W1 (+ W1.5 fix) — UV-C rated-output sheet (§33.6,
 * uvc.rated.outputs.tmpl 697).
 *
 * The UVC "rated outputs" is a 3-tab sheet (Components / Electrical / Notes).
 * The Components tab is a PARTS TABLE with scheduled-qty + an editable actual-qty
 * input per row.
 *
 * ★ W1.5 (W1-4) FIX — the FULL scheduled-row set:
 *   Wave-1 rendered only the 3 *_qtys_model array blocks (power_supply / monitor
 *   / lamp) → 3 rows. OLD renders 7 on the test UVC because the legacy template
 *   (uvc.rated.outputs.tmpl) ALSO emits scalar-derived rows built from the
 *   `<part>_model` + `<part>_scheduled` / `<part>_qty` + `<part>_actual_qty`
 *   fields on the rated model: framing, lamp_holder, balast_connector_kit,
 *   current_sensor, radiometer, hour_meter, toggle_switch,
 *   intlk_contact_controller, intlk_switch, warning_sign. Each renders only when
 *   its `<part>_model` is present (the legacy `{{#if model.<part>_model}}`), in
 *   the exact legacy DOM order:
 *     power_supply(array) → framing(scalar) → monitor(array) → lamp(array) →
 *     lamp_holder → balast → current_sensor → radiometer → hour_meter →
 *     toggle_switch → intlk_contact_controller → intlk_switch → warning_sign.
 *   On uvc 23421 that yields power_supply+framing+monitor+lamp+lamp_holder+
 *   balast+current_sensor = 7 rows (the rest are null → skipped), matching OLD.
 *
 * ★ W1.5 (W1-5) — the "Scheduled Parts Only / All Available Parts" footer toggle.
 *   Legacy lives in uvc.selector.tmpl (.footer-left), display:none until a render
 *   produces outputs (render_outputs sets it block; field changes hide it). It
 *   flips `.uvc-output` between `show_scheduled_parts` (default) and
 *   `show_all_parts`, which CSS uses to show `.scheduled_parts` vs `.all_parts`
 *   rows. Rendered here only when `rated` is present (i.e. there ARE outputs);
 *   hidden in the unrated/empty default state, mirroring legacy.
 *
 * INITIAL/EMPTY STATE: when `rated` is null this renders the empty output shell
 * (tab strip + empty tbody, no footer) — exactly what an unrated-open smoke
 * sees. No write, no rate.
 *
 * The actual-qty inputs are STAGED via onActualQty (keyed <kind>:<catalogId>) and
 * flushed by selectUVC's per-row PATCH loop — never written on edit. Only the
 * array-block rows (power_supply / monitor / lamp) carry catalog ids and stage
 * through onActualQty; the scalar rows' actual-qty inputs are collected by
 * selectUVC from the model fields directly (legacy *_actual_qty payload keys).
 *
 * ★ THE `all_parts` ROWS (this file's second row set).
 *   The footer toggle is CSS-only: `.uvc-output.show_all_parts` hides every
 *   `.scheduled_parts` row and shows every `.all_parts` row (common.css
 *   17447-17458). Legacy emits BOTH sets into the same tbody and lets the class
 *   pick; the port emitted only `.scheduled_parts`, so "All Available Parts"
 *   hid everything and left the Output section blank. Each part therefore now
 *   renders, exactly as uvc.rated.outputs.tmpl does:
 *     1. the `.scheduled_parts` row — sched qty + an EDITABLE actual-qty input
 *     2. the `.all_parts` twin    — the same numbers, both as plain text
 *     3. the `.all_parts` CATALOG rows — every other part the narrowed option
 *        list still allows, at 0 / 0 (legacy `{{#each all_<part>}}`)
 *   Only the lamp block has no mode class at all (tmpl:174 `<div class="row ">`),
 *   so lamp rows — and their input — show in BOTH modes. That is legacy, not an
 *   oversight: dev's "All Available Parts" view shows the lamp row.
 */

import { RatedNotesBlock, formatRatedCell } from './ratingKit';
import type { IUvcRow } from './uvcSelectorApi';

export type OutputTab = 'Components' | 'Electrical' | 'Notes';
type PartsMode = 'scheduled' | 'all';

interface QtyRow {
  id?: number;
  model?: string;
  description?: string;
  qty?: number | string;
  actual_qty?: number | string;
}

interface LampPerRow {
  lamp_model?: string;
  qty?: number | string;
  electrical_wattage?: number | string;
  lamp_wattage?: number | string;
}

/** Handlebars `{{value}}` semantics: null/undefined print as empty, everything
 * else stringifies as-is. Used by the Electrical block, which legacy renders
 * with no `number_output` formatting pass at all. */
const rawCell = (value: unknown): string => (value == null ? '' : String(value));

/** A catalog row behind an `all_parts` 0/0 line (legacy `{{#each all_<part>}}`). */
export interface AllPartRow {
  id?: number | string;
  model?: string;
  description?: string;
}

/**
 * The `all_<part>` lists legacy's AllParts() + render_outputs build and hand to
 * the template. Keyed by the TEMPLATE's variable name, not the catalog's.
 *
 * Two legacy lists are permanently EMPTY and so are absent here:
 *   • `all_toggle_switch` — render_outputs filters `self.all_toggle_switch`
 *     (js:1355) but AllParts never assigns it; it is `undefined` forever.
 *   • `all_inter_lock_switch` — its whole AllParts block is commented out
 *     (js:1085-1096).
 * Both render as nothing in dev. Do not "fix" them here — that would ADD rows
 * dev does not show.
 */
export interface UvcAllParts {
  power_supply: AllPartRow[];
  frameing_kit: AllPartRow[];
  lamp_holder: AllPartRow[];
  balast_conn: AllPartRow[];
  current_sensor: AllPartRow[];
  radio_meter: AllPartRow[];
  hour_meter: AllPartRow[];
  contact_controller: AllPartRow[];
  warning_sign: AllPartRow[];
}

export const EMPTY_ALL_PARTS: UvcAllParts = {
  power_supply: [],
  frameing_kit: [],
  lamp_holder: [],
  balast_conn: [],
  current_sensor: [],
  radio_meter: [],
  hour_meter: [],
  contact_controller: [],
  warning_sign: [],
};

/**
 * Legacy AllParts(): walk the parameter's POSSIBLE-ID list and pull each id's
 * row out of the catalog, in the id list's order (js:968-1084, the repeated
 * `data.forEach(item => coll.toJSON().find(el => el.id === item))` block).
 *
 * `ids == null` means the cascade has not resolved that field yet, which in
 * legacy leaves `self.all_<part>` undefined — and render_outputs' `if (self.…)`
 * guard then keeps the array EMPTY. So null ⇒ no rows, deliberately.
 *
 * Legacy pushes the `find` result unconditionally, so an id with no catalog row
 * lands as `undefined` and the template draws a blank line; we drop those (a
 * blank line is not a behaviour worth porting, and dev3's catalogs are complete).
 */
export function narrowCatalog(
  catalog: AllPartRow[],
  ids: Array<number | string> | null,
  key: keyof AllPartRow = 'id'
): AllPartRow[] {
  if (!ids) return [];
  return ids
    .map((id) => catalog.find((r) => String(r[key]) === String(id)))
    .filter((r): r is AllPartRow => r != null);
}

/** render_outputs' `filter(item => item.model !== <selected>)` (js:1355-1449). */
export function excludeSelectedModel(
  rows: AllPartRow[],
  selectedModel: unknown
): AllPartRow[] {
  if (selectedModel == null || selectedModel === '') return rows;
  return rows.filter((r) => r.model !== selectedModel);
}

/** A scalar-derived scheduled row descriptor (legacy `{{#if model.X_model}}`). */
interface ScalarPartSpec {
  /** the rated-model field holding the part model name. */
  modelKey: string;
  /** the description field. */
  descKey: string;
  /** the scheduled-qty field. */
  schedKey: string;
  /** the actual-qty field (defaults to scheduled when null). */
  actualKey: string;
  /** the <input name> (legacy actual-qty input name — selectUVC reads these). */
  inputName: string;
  /** which `all_<part>` catalog list follows this part's rows (omitted for the
   * two lists legacy leaves permanently empty — see UvcAllParts). */
  allKey?: keyof UvcAllParts;
  /** legacy emits the catalog rows BEFORE this part's own rows (balast only —
   * `{{#each all_balast_conn}}` sits at tmpl:238, above the part at :255). */
  catalogFirst?: boolean;
  /** legacy emits this part's `all_parts` twin OUTSIDE the `{{#if X_model}}`
   * (balast only, tmpl:255) — so in all-parts mode dev draws a blank row when
   * no balast kit is selected. Preserved verbatim. */
  allRowUnconditional?: boolean;
}

/**
 * The scalar scheduled rows in legacy DOM order (uvc.rated.outputs.tmpl). Each
 * appears only when its modelKey is truthy. Field names verbatim from the
 * template's `{{model.X}}` bindings (note the legacy mis-spellings: frameing,
 * balast, radiometer; current_sensor uses `current_sensor_qty` not `_scheduled`).
 */
const FRAMING_SPEC: ScalarPartSpec = {
  modelKey: 'frameing_kit_model',
  descKey: 'frameing_kit_description',
  schedKey: 'frameing_kit_scheduled',
  actualKey: 'frameing_kit_actual_qty',
  inputName: 'frameing_kit_actual_qty',
  allKey: 'frameing_kit',
};
const POST_LAMP_SPECS: ScalarPartSpec[] = [
  {
    modelKey: 'lamp_holder_model',
    descKey: 'lamp_holder_description',
    schedKey: 'lamp_holder_kit_scheduled',
    actualKey: 'lamp_holder_actual_qty',
    inputName: 'lamp_holder_actual_qty',
    allKey: 'lamp_holder',
  },
  {
    modelKey: 'balast_connector_kit_model',
    descKey: 'balast_connector_kit_model_description',
    schedKey: 'balast_connector_kit_scheduled',
    actualKey: 'balast_connector_kit_actual_qty',
    inputName: 'balast_connector_kit_actual_qty',
    allKey: 'balast_conn',
    catalogFirst: true,
    allRowUnconditional: true,
  },
  {
    modelKey: 'current_sensor_model',
    descKey: 'current_sensor_description',
    schedKey: 'current_sensor_qty',
    actualKey: 'current_sensor_actual_qty',
    inputName: 'current_sensor_actual_qty',
    allKey: 'current_sensor',
  },
  {
    modelKey: 'radiometer_model',
    descKey: 'radiometer_description',
    schedKey: 'radio_meter_scheduled',
    actualKey: 'radiometer_actual_qty',
    inputName: 'radiometer_actual_qty',
    allKey: 'radio_meter',
  },
  {
    modelKey: 'hour_meter_model',
    descKey: 'hour_meter_description',
    schedKey: 'hour_meter_scheduled',
    actualKey: 'hour_meter_actual_qty',
    inputName: 'hour_meter_actual_qty',
    allKey: 'hour_meter',
  },
  {
    modelKey: 'toggle_switch_model',
    descKey: 'toggle_switch_description',
    schedKey: 'toggle_switch_scheduled',
    actualKey: 'toggle_switch_actual_qty',
    inputName: 'toggle_switch_actual_qty',
  },
  {
    modelKey: 'intlk_contact_controller_model',
    descKey: 'intlk_contact_controller_description',
    schedKey: 'intlk_contact_controller_scheduled',
    actualKey: 'intlk_contact_controller_actual_qty',
    inputName: 'intlk_contact_controller_actual_qty',
    allKey: 'contact_controller',
  },
  {
    modelKey: 'intlk_switch_model',
    descKey: 'intlk_switch_description',
    schedKey: 'inter_lock_switch_scheduled',
    actualKey: 'intlk_switch_actual_qty',
    inputName: 'intlk_switch_actual_qty',
  },
  {
    modelKey: 'warning_sign_model',
    descKey: 'warning_sign_description',
    schedKey: 'warning_sign_scheduled',
    actualKey: 'warning_sign_actual_qty',
    inputName: 'warning_sign_actual_qty',
    allKey: 'warning_sign',
  },
];

export const UvcRatedOutputs = ({
  rated,
  allParts = EMPTY_ALL_PARTS,
  onActualQty,
  partsMode,
  tab,
  onTab,
}: {
  rated: IUvcRow | null;
  /** the `all_<part>` catalog lists (legacy AllParts() + render_outputs). Built
   * in UvcSelector, where the catalogs and the cascade's id lists live. */
  allParts?: UvcAllParts;
  onActualQty: (
    kind: 'power_supply' | 'monitor' | 'lamp',
    catalogId: number,
    qty: string
  ) => void;
  /** owned by UvcSelector — it carries the class on `.output-part`. */
  partsMode: PartsMode;
  /** also owned by UvcSelector — `.footer-left` is a sibling and follows it. */
  tab: OutputTab;
  onTab: (t: OutputTab) => void;
}) => {

  const m = rated ?? {};
  const powerSupplyRows = (m.power_supply_qtys_model as QtyRow[] | undefined) ?? [];
  const monitorRows = (m.monitor_qtys_model as QtyRow[] | undefined) ?? [];
  const lampRows = (m.lamp_qtys_model as QtyRow[] | undefined) ?? [];
  const rows = Number(m.rows ?? 0);
  const lampsPerRow = (m.lamps_per_row as LampPerRow[] | undefined) ?? [];
  const notes = ((m.notes as Array<{ note: string }> | undefined) ?? [])
    .slice()
    .reverse();

  const tabClass = (t: OutputTab) =>
    `tab-pane fade col-xs-12 float-left${tab === t ? ' in active' : ''}`;

  // an editable scheduled/actual parts block fed by a *_qtys_model array
  // (power_supply / monitor / lamp). These rows carry a catalog id and stage
  // their actual-qty edits through onActualQty.
  //
  // `rowClass` is the mode class: legacy gives power_supply and monitor
  // `scheduled_parts` but the LAMP block a bare `row` (tmpl:174), so lamp rows
  // stay visible — and editable — under "All Available Parts" too.
  const QtyBlock = ({
    kind,
    rows: blockRows,
    rowClass = 'row scheduled_parts',
  }: {
    kind: 'power_supply' | 'monitor' | 'lamp';
    rows: QtyRow[];
    rowClass?: string;
  }) => (
    <>
      {blockRows.map((r, i) => (
        <div className={rowClass} key={`${kind}-${r.id ?? i}`}>
          <div className="col-xs-3 top-padding"><span>{r.model}</span></div>
          <div className="col-xs-6 top-padding"><span>{r.description}</span></div>
          <div className="col-xs-3">
            <div className="row">
              <div className="col-xs-6 text-right top-padding">
                <span>{formatRatedCell(r.qty, 'integer')}</span>
              </div>
              <div className="col-xs-6 text-right">
                <input
                  data-id={r.id}
                  type="text"
                  name={`${kind}_actual_qty`}
                  className="text-right actual_qty"
                  defaultValue={String(r.actual_qty ?? r.qty ?? '')}
                  onChange={(e) =>
                    r.id != null && onActualQty(kind, r.id, e.target.value)
                  }
                />
              </div>
            </div>
          </div>
        </div>
      ))}
    </>
  );

  // the read-only `all_parts` twin of a QtyBlock (tmpl:62 power_supply,
  // tmpl:157 monitor). Same numbers, no input — and the monitor twin prints a
  // LITERAL 0 in SCHED. QTY rather than the row's qty (tmpl:164), which is why
  // dev's all-parts view shows the UVRepeat kit as "0 / 1".
  const QtyBlockAll = ({
    kind,
    rows: blockRows,
    zeroSched = false,
  }: {
    kind: 'power_supply' | 'monitor';
    rows: QtyRow[];
    zeroSched?: boolean;
  }) => (
    <>
      {blockRows.map((r, i) => (
        <div className="row all_parts" key={`${kind}-all-${r.id ?? i}`}>
          <div className="col-xs-3 top-padding"><span>{r.model}</span></div>
          <div className="col-xs-6 top-padding"><span>{r.description}</span></div>
          <div className="col-xs-3">
            <div className="row">
              <div className="col-xs-6 text-right top-padding">
                {zeroSched ? '0' : formatRatedCell(r.qty, 'integer')}
              </div>
              <div className="col-xs-6 text-right top-padding">
                {formatRatedCell(r.actual_qty ?? r.qty, 'integer')}
              </div>
            </div>
          </div>
        </div>
      ))}
    </>
  );

  // the `all_<part>` catalog rows — every still-available part that was NOT
  // selected, at 0 / 0 (legacy `{{#each all_<part>}}`, e.g. tmpl:78).
  const CatalogRows = ({ rows: catRows, kind }: { rows: AllPartRow[]; kind: string }) => (
    <>
      {catRows.map((r, i) => (
        <div className="row all_parts" key={`${kind}-cat-${r.id ?? i}`}>
          <div className="col-xs-3 top-padding">{r.model}</div>
          <div className="col-xs-6 top-padding">{r.description}</div>
          <div className="col-xs-3">
            <div className="row">
              <div className="col-xs-6 text-right top-padding">0</div>
              <div className="col-xs-6 text-right top-padding">0</div>
            </div>
          </div>
        </div>
      ))}
    </>
  );

  // a scalar-derived scheduled row (framing / lamp_holder / balast / current_
  // sensor / radiometer / hour_meter / toggle_switch / intlk_* / warning_sign).
  // Rendered only when its modelKey is present (legacy `{{#if model.X_model}}`).
  // The actual-qty input is uncontrolled and read by selectUVC from the model
  // fields (no catalog id to stage through onActualQty); editing it is a code-
  // path verified by review and parked to write-QA.
  const ScalarRow = ({ spec }: { spec: ScalarPartSpec }) => {
    const model = m[spec.modelKey];
    if (model == null || model === '') return null;
    const sched = m[spec.schedKey];
    const actual = m[spec.actualKey];
    return (
      <div className="row scheduled_parts">
        <div className="col-xs-3 top-padding"><span>{String(model)}</span></div>
        <div className="col-xs-6 top-padding"><span>{String(m[spec.descKey] ?? '')}</span></div>
        <div className="col-xs-3">
          <div className="row">
            <div className="col-xs-6 text-right top-padding">
              <span>{formatRatedCell(sched, 'integer')}</span>
            </div>
            <div className="col-xs-6 text-right">
              <input
                type="text"
                name={spec.inputName}
                className="text-right actual_qty"
                defaultValue={String((actual ?? sched) ?? '')}
              />
            </div>
          </div>
        </div>
      </div>
    );
  };

  // the read-only `all_parts` twin of a ScalarRow — same two numbers, no input.
  const ScalarRowAll = ({ spec }: { spec: ScalarPartSpec }) => {
    const model = m[spec.modelKey];
    if (!spec.allRowUnconditional && (model == null || model === '')) return null;
    const sched = m[spec.schedKey];
    const actual = m[spec.actualKey];
    return (
      <div className="row all_parts">
        <div className="col-xs-3 top-padding"><span>{String(model ?? '')}</span></div>
        <div className="col-xs-6 top-padding"><span>{String(m[spec.descKey] ?? '')}</span></div>
        <div className="col-xs-3">
          <div className="row">
            <div className="col-xs-6 text-right top-padding">
              <span>{formatRatedCell(sched, 'integer')}</span>
            </div>
            <div className="col-xs-6 text-right top-padding">
              {formatRatedCell(actual ?? sched, 'integer')}
            </div>
          </div>
        </div>
      </div>
    );
  };

  /** one scalar part's three row sets, in the legacy template's order. */
  const ScalarPart = ({ spec }: { spec: ScalarPartSpec }) => {
    const catalog = spec.allKey ? (
      <CatalogRows rows={allParts[spec.allKey]} kind={spec.allKey} />
    ) : null;
    return (
      <>
        {spec.catalogFirst && catalog}
        <ScalarRow spec={spec} />
        <ScalarRowAll spec={spec} />
        {!spec.catalogFirst && catalog}
      </>
    );
  };

  // ★ NO WRAPPER ELEMENT. render_outputs injects this template straight into
  // `.output-part` (uvc.selector.js:1452), so the legacy DOM has exactly ONE
  // `.uvc-output.rating` — the `.output-part` itself. The port added a second
  // one here, and `#rating_mode_uvc_content .uvc-output.rating { height:
  // calc(100% - 360px) }` (common.css:17441) then applied TWICE: 743 → 383 →
  // 23px, so `div.full-height` (overflow:auto) clipped at 552 and only the
  // table header cleared it. Every rated row, both other tabs and the footer
  // were rendered and invisible. A fragment keeps the single container.
  return (
    <>
      <div className="col-xs-1 nav nav-tabs">
        <ul>
          <li className="cursor-default">Output</li>
          {(['Components', 'Electrical', 'Notes'] as OutputTab[]).map((t) => (
            <li key={t} className={tab === t ? 'active' : undefined}>
              <a
                className={`output_tab ${t.toLowerCase()}${tab === t ? ' active' : ''}`}
                onClick={() => onTab(t)}
              >
                {t}
              </a>
            </li>
          ))}
        </ul>
      </div>

      <div className="col-xs-11 with-border">
        <div className="row">
          {m.error ? (
            <div className="error-message">
              Unable to find a uv-c that satisfies all inputted parameters.
            </div>
          ) : (
            <div className="full-height">
              {/* ----- Components ----- */}
              <div className={tabClass('Components')} id="Properties_output">
                <div className={`row full-height ${partsMode === 'all' ? 'all' : 'schedul'}`}>
                  <div className="col-xs-12 thead-part">
                    <div className="row">
                      <div className="col-xs-3">MODEL</div>
                      <div className="col-xs-6">DESCRIPTION</div>
                      <div className="col-xs-3">
                        <div className="row">
                          <div className="col-xs-6 text-right">SCHED. QTY</div>
                          <div className="col-xs-6 text-right">ACTUAL QTY</div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="col-xs-12 tbody-part">
                    {/* legacy DOM order: power_supply → framing → monitor →
                        lamp → lamp_holder → balast → current_sensor →
                        radiometer → hour_meter → toggle_switch →
                        intlk_contact_controller → intlk_switch → warning_sign.
                        Each part emits its `scheduled_parts` row, its read-only
                        `all_parts` twin and then the `all_<part>` catalog rows;
                        the CSS mode class on `.uvc-output` picks which show. */}
                    <QtyBlock kind="power_supply" rows={powerSupplyRows} />
                    <QtyBlockAll kind="power_supply" rows={powerSupplyRows} />
                    <CatalogRows rows={allParts.power_supply} kind="power_supply" />
                    <ScalarPart spec={FRAMING_SPEC} />
                    <QtyBlock kind="monitor" rows={monitorRows} />
                    <QtyBlockAll kind="monitor" rows={monitorRows} zeroSched />
                    {/* the lamp block has NO mode class in legacy — both modes */}
                    <QtyBlock kind="lamp" rows={lampRows} rowClass="row" />
                    {POST_LAMP_SPECS.map((spec) => (
                      <ScalarPart key={spec.modelKey} spec={spec} />
                    ))}
                  </div>
                </div>
              </div>

              {/* ----- Electrical ----- */}
              <div className={tabClass('Electrical')} id="Electrical_output">
                <div className="row full-height">
                  <div className="col-xs-12 thead-part">
                    <div className="row">
                      <div className="col-xs-1">ROW</div>
                      <div className="col-xs-5">FIXTURE</div>
                      <div className="col-xs-2 text-right">QTY</div>
                      <div className="col-xs-2 text-right">ELECTRICAL (W)</div>
                      <div className="col-xs-2 text-right">LAMP (W)</div>
                    </div>
                  </div>
                  {/* legacy prints these raw — the Electrical block of
                      uvc.rated.outputs.tmpl (:639-657) carries no
                      `number_output` span, so no toFixed(2)/comma pass ever
                      runs over it. Rendering the value straight is the parity
                      behaviour: 370, not 370.00. */}
                  <div className="col-xs-12 tbody-part">
                    {Array.from({ length: rows }, (_, ri) =>
                      lampsPerRow.map((lp, li) => (
                        <div className="row lamps_per_row" key={`${ri}-${li}`}>
                          <div className="col-xs-1">{ri + 1}</div>
                          <div className="col-xs-5"><span>{lp.lamp_model}</span></div>
                          <div className="col-xs-2 text-right"><span>{rawCell(lp.qty)}</span></div>
                          <div className="col-xs-2 text-right"><span>{rawCell(lp.electrical_wattage)}</span></div>
                          <div className="col-xs-2 text-right"><span>{rawCell(lp.lamp_wattage)}</span></div>
                        </div>
                      ))
                    )}
                  </div>
                  <div className="footer-part col-xs-12">
                    <div className="row">
                      <div className="col-xs-2 text-right col-xs-offset-6"><span>Total Power (W)</span></div>
                      <div className="col-xs-2 text-right"><span>{rawCell(m.lamp_total_electrical_wattage)}</span></div>
                      <div className="col-xs-2 text-right"><span>{rawCell(m.lamp_total_wattage)}</span></div>
                    </div>
                  </div>
                </div>
              </div>

              {/* ----- Notes ----- */}
              <div className={tabClass('Notes')} id="Notes_output">
                <div className="col-xs-12">
                  <div className="row">
                    <div className="col-xs-12">
                      <RatedNotesBlock notes={notes} />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

/** `.footer-left` and `.rating-time` are SIBLINGS of `.output-part` in the
 * legacy template (uvc.selector.tmpl:425 / :428, both after the `.output-part`
 * div closes at :424) — they are not part of the injected rated-output sheet.
 * Rendered by UvcSelector; kept here next to the sheet they belong to.
 *
 * `#rating_mode_uvc_content .footer-left` is `display:none` in CSS
 * (common.css:17542); legacy un-hides it with an inline
 * `css('display','block')` from the rate's `.always` (js:1298) and re-hides it
 * on every field change. The port rendered it conditionally and never set the
 * inline style, so the stylesheet kept it hidden even after a successful rate.
 *
 * WHEN it renders is UvcSelector's `footerVisible` — deliberately a latch on
 * legacy's two un-hide calls (the rate `.always` and an output-tab click), NOT
 * a derivation from `rated` + the active tab. Reopening an already-selected UVC
 * restores the rated sheet through a render_outputs path whose `.footer-left`
 * un-hide line is commented out (js:1838-1847), so legacy shows the parts table
 * with NO footer until an output tab is clicked. */
export const UvcOutputFooter = ({
  partsMode,
  onPartsMode,
}: {
  partsMode: PartsMode;
  onPartsMode: (m: PartsMode) => void;
}) => (
  <div className="col-xs-8 footer-part footer-left" style={{ display: 'block' }}>
    <button
      type="button"
      name="show_scheduled_parts"
      className={`btn show_parts scheduled_parts${partsMode === 'scheduled' ? ' active' : ''}`}
      onClick={() => onPartsMode('scheduled')}
    >
      Scheduled Parts Only
    </button>
    <button
      type="button"
      className={`btn show_parts all_parts${partsMode === 'all' ? ' btn-active' : ''}`}
      onClick={() => onPartsMode('all')}
    >
      All Available Parts
    </button>
  </div>
);

export default UvcRatedOutputs;
